import * as THREE from "three";
import axios from "axios";
import { GetLatLon, SetMapCustomSize } from "./MapControl";
import { Loader } from "@googlemaps/js-api-loader";
import { SetSunVecArr } from "../Sun-Vector-Path/LoadSun";
import sleep from "../../../../../helpers/sleep";
import { logger } from "../../../../../services/loggingService";
import { Viewer } from "../Viewer-Factory/Viewer";
import { Coordinates, TerrainNode, TreeGroup, XZCoordinates } from "../../../../../@types/types";
import { CustomImageProperty } from "../../../../../@types/enums";

export const mapSize = 200;
const terrainExaggerationFactor = 6;
export const pixelCount = 10, lineCount = pixelCount + 1, apiCount = 512, lastNum = pixelCount * pixelCount, pixelSize = mapSize / pixelCount;

export const customMapSettingList = {
    settingScale: { title: 'Scale', id: 'Scale', settings: { min: 1, max: 5, step: 0.01 }, label: '', default: 1 },
    settingRot: { title: 'Rotation', id: 'Rotation', settings: { min: 0, max: 360, step: 1 }, label: 'deg', default: 0 }
};

const apiDelayTime = 10;
const apiDelayFailTime = 3000;

export async function SetTerrain(self: Viewer, origin: Coordinates) {
    try {
        if(!self.mapCustom){
            throw new Error("mapCustom is missing");
        }
        self.mapCustom.scale.set(1, 1, 1);
        self.mapCustom.visible = false;
        const tempArr = initializeTempArr();

        self.nodeArr = [{ x: 0, y: 0, z: 0 }];
        SetTerrainMapGeo(self, tempArr);
        self.loadingTerrain = true;

        const latlonArr = generateLatLonArr(origin);
        const googleMapsLoader = new Loader({
            apiKey: import.meta.env.VITE_GOOGLE_JAVASCRIPT_MAP_API_KEY,
            version: "weekly",
        });

        const google = await googleMapsLoader.load();
        const elevator = new google.maps.ElevationService();

        await CallLatLonAPITotal(elevator, self, latlonArr, []);
        await fetchAddress(self, origin);

    } catch (error) {
        logger.error(error);
        window.alert('Failed to load terrain!');
    }
}

async function fetchAddress(self: Viewer, origin: Coordinates) {
    try {
        const { latitude, longitude } = origin;
        const response = await axios.get(`${import.meta.env.VITE_GOOGLE_MAP_INFO_API_URL}?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_JAVASCRIPT_MAP_API_KEY}`);

        if (response.status === 200 && response.data?.results?.length) {
            const addressObj = response.data.results[0].address_components;
            const addressInfo = extractAddressInfo(addressObj);
            self.addressInfo = addressInfo;
        }
    } catch (error) {
        logger.error(error);
    }
}

export function loadGoogleMapsAPI(apiKey: string) {
    return new Promise((resolve, reject) => {
        if (typeof window.google !== 'undefined') {
            resolve(window.google);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=elevation&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;

        script.onerror = (error) => reject(error);

        window.initGoogleMaps = () => {
            resolve(window.google);
        };

        document.head.appendChild(script);
    });
}

function extractAddressInfo(addressObj: google.maps.GeocoderAddressComponent[]) {
    const country = addressObj[6]?.long_name;
    const state = addressObj[5]?.long_name;
    const city = addressObj[4]?.long_name;
    const detail = addressObj[3]?.long_name;
    const postalCode = addressObj[7]?.long_name;
    return { country, state, city, detail, postalCode };
}

function initializeTempArr(): TerrainNode[] {
    const tempArr = new Array(lineCount * lineCount);
    for (let x = 0; x < lineCount; x++) {
        for (let z = 0; z < lineCount; z++) {
            tempArr[x * lineCount + z] = { x, z, py: 0.1 };
        }
    }
    return tempArr;
}

function generateLatLonArr(origin: Coordinates) {
    const latlonArr = [];
    const tempVec = new THREE.Vector3(0, 0, 0);
    for (let z = mapSize / -2; z <= mapSize / 2; z += pixelSize) {
        for (let x = mapSize / -2; x <= mapSize / 2; x += pixelSize) {
            tempVec.set(x || pixelSize, 0, z || pixelSize);
            latlonArr.push(GetLatLon(origin, tempVec));
        }
    }
    return latlonArr;
}

async function CallLatLonAPITotal(elevator: google.maps.ElevationService, self: Viewer, latlonArr: Coordinates[], terrainData: TerrainNode[]) {
    let startNum = 0;

    while (startNum + apiCount < lineCount * lineCount) {
        try {
            const reqArr = prepareRequestArray(latlonArr, startNum);
            if (startNum > 0) {
                await sleep(apiDelayTime);
            }

            const res = await elevator.getElevationForLocations({ 'locations': reqArr });
            updateTerrainData(res, startNum, terrainData);

            startNum += apiCount;
        } catch (error) {
            logger.error(error);
            await sleep(apiDelayFailTime);
        }
    }

    const customData = CustomTerrainData(terrainData);
    self.customTerrainData = customData;
    self.loadingTerrain = false;
    SetTerrainMapGeo(self, customData);
}

function prepareRequestArray(latlonArr: Coordinates[], startNum: number) {
    const reqArr = [];
    for (let i = 0; i < apiCount; i++) {
        const location = latlonArr[startNum + i];
        if (!location) continue;
        reqArr.push({ lat: location.latitude, lng: location.longitude });
    }
    return reqArr;
}

function updateTerrainData(res: google.maps.LocationElevationResponse, startNum: number, terrainData: TerrainNode[]) {
    res.results.forEach((item, idx: number) => {
        const nodeNum = startNum + idx;
        const nodeX = nodeNum % lineCount, nodeZ = Math.floor(nodeNum / lineCount);
        terrainData[nodeNum] = { x: nodeX, z: nodeZ, py: item.elevation };
    });
}

function CustomTerrainData(terrainData: TerrainNode[]): TerrainNode[] {
    let minY = Infinity, maxY = -Infinity;
    terrainData.forEach(item => {
        if (item.py < minY) minY = item.py;
        if (item.py > maxY) maxY = item.py;
    });
    const middleY = (minY + maxY) / 2;

    return terrainData.map(item => {
        const dY = middleY - item.py;
        return { ...item, py: middleY - dY / terrainExaggerationFactor };
    });
}

function SetTerrainMapGeo(self: Viewer, terrainData: TerrainNode[]) {
    const { nodeArr } = self;

    const nodeAltArr = nodeArr.map(node => GetAlt(terrainData, node));
    const buildingAltL = Math.min(...nodeAltArr);
    const buildingAltH = Math.max(...nodeAltArr);

    self.terrainArr = [];
    const planeGeo = new THREE.PlaneGeometry(mapSize, mapSize, pixelCount, pixelCount);
    const customGeo = new THREE.PlaneGeometry(mapSize, mapSize, pixelCount, pixelCount);
    const verPlane = planeGeo.attributes.position.array;
    const verCustom = customGeo.attributes.position.array;

    for (let i = 0; i < terrainData.length; i++) {
        const { py } = terrainData[i];
        verPlane[3 * i + 2] = py * 1;
        verCustom[3 * i + 2] = py * 1 + 1;
        const z = Math.floor(i / lineCount), x = i % lineCount;
        const pz = pixelSize * z - mapSize / 2, px = pixelSize * x - mapSize / 2;
        self.terrainArr.push({ x, z, px, py, pz });
    }

    if(!self.mapCustom) {
        logger.error("Error fetching mapCustom")
        return;
    } 
    self.mapCustom.geometry = planeGeo;
    const customScl = self.mapCustom?.scale;
    if(!customScl) {
        logger.error("Error fetching custom scale")
        return;
    } 
    const customWidth = customScl.x * mapSize;
    const customHeight = customScl.y * mapSize;
    SetMapCustomSize(self, CustomImageProperty.width, customWidth);
    SetMapCustomSize(self, CustomImageProperty.height, customHeight);

    self.buildingAlt = { low: buildingAltL, high: buildingAltH };
    SetSunVecArr(self);
    if (nodeArr.some(node => node.x === undefined || node.z === undefined)) return;
    SetTerrainPosition(self);
}

export function SetTerrainPosition(self: Viewer) {
    const { treeGroup, terrainArr, terrainView, viewMode, buildingAlt, roofGroup } = self;
    const terrainFlag = terrainView && viewMode === '3d';
    if (buildingAlt === undefined || buildingAlt === null || !self.mapCustom) return;

    const deltaBuildingH = buildingAlt.low + 0.2;
    treeGroup.children.forEach((tree: any) => {
        tree.position.y = terrainFlag ? GetAlt(terrainArr, tree.position, deltaBuildingH) : 0;
    });

    self.mapCustom.scale.z = terrainFlag ? 1 : 0.01;
    self.mapCustom.position.y = terrainFlag ? -deltaBuildingH : 0;

    if (!roofGroup.children[0]) return;
    roofGroup.children[0].children.forEach(child => {
        if ((child as THREE.Object3D & {meshType?: string}).meshType !== 'floor') return;
        child.scale.z = (buildingAlt.high - buildingAlt.low) * 10;
    });
}

export function GetAlt(terrainData: TerrainNode[], pos: XZCoordinates, delta = 0) {
    const { x, z } = pos;
    const xn = Math.round((x + mapSize / 2) / pixelSize);
    const zn = Math.round((z + mapSize / 2) / pixelSize);
    const terrainNode = terrainData.find(node => node && node.x === xn && node.z === zn);
    return terrainNode ? terrainNode.py - delta : 0;
}
